import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getSessionEvents, getSessionConsoleLogs, analyzeSession, querySession, type SessionEvent, type ConsoleLogEntry, type SessionAnalysis } from '../../api/client';
import {
  ArrowLeft,
  MousePointerClick,
  Terminal,
  Info,
  Loader2,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Target,
  Smartphone,
  TrendingUp,
  XCircle,
  Lightbulb,
  User,
  Send,
  MessageSquare,
} from 'lucide-react';
import clsx from 'clsx';
import { formatDateTime } from '../../utils/formatters';
import MobileReplay, { isMobileReplayEvent, MOBILE_EVENT_LABELS } from './MobileReplay';

interface SessionReplayProps {
  sessionId: string;
  autoPlay?: boolean;
}

type Tab = 'events' | 'console' | 'details' | 'ai';

export default function SessionReplay({ sessionId, autoPlay = false }: SessionReplayProps) {
  const [activeTab, setActiveTab] = useState<Tab>('events');
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);

  const { data: rawEvents, isLoading, error } = useQuery({
    queryKey: ['session-events', sessionId],
    queryFn: () => getSessionEvents(sessionId),
  });

  const { data: consoleLogs } = useQuery({
    queryKey: ['session-console', sessionId],
    queryFn: () => getSessionConsoleLogs(sessionId),
  });

  const [aiAnalysis, setAiAnalysis] = useState<SessionAnalysis | null>(null);
  const analyzeMutation = useMutation({
    mutationFn: () => analyzeSession(sessionId),
    onSuccess: (data) => setAiAnalysis(data),
  });

  // Follow-up query state
  const [queryInput, setQueryInput] = useState('');
  const [queryHistory, setQueryHistory] = useState<Array<{ question: string; answer: string }>>([]);
  const queryMutation = useMutation({
    mutationFn: (question: string) =>
      querySession(sessionId, question, (aiAnalysis as any)?.narrative),
    onSuccess: (data, question) => {
      setQueryHistory((prev) => [...prev, { question, answer: data.answer }]);
      setQueryInput('');
    },
  });

  // Mobile app sessions (Fitnessage RN replay SDK) store timeline events with
  // custom types >= 100 — they get their own player instead of rrweb.
  const mobileEvents = (rawEvents || []).filter(
    (e: any) => isMobileReplayEvent(e.type) || isMobileReplayEvent(e.data?.type),
  ).map((e: any) => (isMobileReplayEvent(e.type) ? e : e.data));
  const isMobileSession = mobileEvents.length > 0;

  // Extract rrweb events from the API response
  const rrwebEvents = (rawEvents || [])
    .filter((e: any) => {
      if (isMobileReplayEvent(e.type) || isMobileReplayEvent(e.data?.type)) return false;
      // Handle both direct rrweb events and wrapped events
      if (e.data?.type !== undefined && e.data?.data !== undefined) return true; // wrapped rrweb event
      if (e.type !== undefined && e.timestamp !== undefined && e.data !== undefined) return true; // direct rrweb event
      return false;
    })
    .map((e: any) => {
      // If it's a wrapped event from our API (has eventType + data fields)
      if (e.data?.data && e.data?.type !== undefined) {
        return e.data; // the actual rrweb event is in e.data
      }
      // If the event itself has the rrweb shape
      if (e.eventType !== undefined && e.data) {
        return { type: e.eventType, timestamp: Number(e.timestamp), data: e.data };
      }
      return e;
    })
    .sort((a: any, b: any) => (a.timestamp || 0) - (b.timestamp || 0));

  // Non-rrweb events for the sidebar
  const sidebarEvents = (rawEvents || [])
    .filter((e: any) => {
      const t = e.type || e.eventType;
      // Frame events (106) are pixel payloads for the player, noise as list rows
      return t !== 'rrweb' && t !== undefined && t !== 106;
    })
    .sort((a: any, b: any) => {
      const ta = a.timestamp || new Date(a.createdAt).getTime() || 0;
      const tb = b.timestamp || new Date(b.createdAt).getTime() || 0;
      return ta - tb;
    });

  useEffect(() => {
    if (!playerContainerRef.current || rrwebEvents.length === 0) return;

    // Clean up previous player
    if (playerRef.current) {
      playerContainerRef.current.innerHTML = '';
      playerRef.current = null;
    }

    // Dynamically import rrweb-player
    import('rrweb-player').then((mod) => {
      const RRWebPlayer = mod.default || mod;

      // Import the CSS (ignore TS error for CSS module)
      // @ts-ignore
      import('rrweb-player/dist/style.css').catch(() => {});

      if (!playerContainerRef.current) return;

      try {
        const player = new RRWebPlayer({
          target: playerContainerRef.current,
          props: {
            events: rrwebEvents,
            width: playerContainerRef.current.clientWidth,
            height: playerContainerRef.current.clientHeight - 80,
            autoPlay: autoPlay,
            showController: true,
            speedOption: [1, 2, 4, 8],
            skipInactive: true,
            showWarning: false,
            showDebug: false,
          },
        });

        playerRef.current = player;
      } catch (err) {
        console.error('[Vantage] Failed to initialize rrweb-player:', err);
      }
    }).catch((err) => {
      console.error('[Vantage] Failed to load rrweb-player:', err);
    });

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.$destroy?.();
        } catch {}
        playerRef.current = null;
      }
    };
  }, [rrwebEvents.length]);

  const tabs: Array<{ id: Tab; label: string; icon: React.ReactNode }> = [
    { id: 'events', label: 'Events', icon: <MousePointerClick className="h-4 w-4" /> },
    { id: 'console', label: 'Console', icon: <Terminal className="h-4 w-4" /> },
    { id: 'details', label: 'Details', icon: <Info className="h-4 w-4" /> },
    { id: 'ai', label: 'AI', icon: <Sparkles className="h-4 w-4" /> },
  ];

  return (
    <div className="flex gap-4 h-[calc(100vh-10rem)]">
      {/* Player Area */}
      <div className="flex flex-1 flex-col">
        <div className="relative flex-1 overflow-hidden rounded-xl border border-gray-200 bg-gray-900">
          {isLoading && (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
              <span className="ml-3 text-white">Loading session events...</span>
            </div>
          )}

          {error && (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-red-400">Failed to load session events</p>
            </div>
          )}

          {!isLoading && !error && !isMobileSession && rrwebEvents.length === 0 && (
            <div className="flex h-full w-full items-center justify-center text-center">
              <div>
                <p className="text-sm text-gray-400">No recording data available for this session</p>
                <p className="mt-2 text-xs text-gray-500">
                  This session may not have been recorded (sampling rate or recording disabled)
                </p>
              </div>
            </div>
          )}

          {/* Mobile app sessions get the interaction-timeline player */}
          {!isLoading && !error && isMobileSession && <MobileReplay events={mobileEvents} />}

          {/* rrweb-player mounts here */}
          <div
            ref={playerContainerRef}
            className="h-full w-full"
            style={{ display: !isMobileSession && rrwebEvents.length > 0 ? 'block' : 'none' }}
          />
        </div>
      </div>

      {/* Side Panel */}
      <div className="w-72 flex-shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="overflow-y-auto h-[calc(100%-3rem)]">
          {activeTab === 'events' && (
            <div className="p-2 space-y-1">
              {sidebarEvents.length === 0 && (
                <p className="p-4 text-center text-xs text-gray-400">No events loaded</p>
              )}
              {sidebarEvents.map((event: any, i: number) => (
                <div
                  key={event.id || i}
                  className="flex items-start gap-2 rounded-lg p-2 text-xs hover:bg-gray-50"
                >
                  <span className="mt-0.5 shrink-0 text-gray-400">
                    {event.timestamp
                      ? new Date(Number(event.timestamp)).toLocaleTimeString()
                      : event.createdAt
                        ? new Date(event.createdAt).toLocaleTimeString()
                        : '—'}
                  </span>
                  <div>
                    <span className="font-medium text-gray-700">
                      {MOBILE_EVENT_LABELS[event.type as number] || event.type || event.eventType || 'event'}
                    </span>
                    {(event.urlPath || event.data?.path || event.data?.message || event.data?.name) && (
                      <p className="mt-0.5 font-mono text-gray-400 break-all">
                        {event.urlPath || event.data?.path || event.data?.message || event.data?.name}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'console' && (
            <div className="p-2 space-y-0.5">
              {(!consoleLogs || consoleLogs.length === 0) && (
                <p className="p-4 text-center text-xs text-gray-400">No console logs captured</p>
              )}
              {(consoleLogs || []).map((log: ConsoleLogEntry) => (
                <div
                  key={log.id}
                  className={clsx(
                    'rounded px-2 py-1.5 text-xs font-mono break-all',
                    log.level === 'error' && 'bg-red-50 text-red-700 border-l-2 border-red-400',
                    log.level === 'warn' && 'bg-yellow-50 text-yellow-700 border-l-2 border-yellow-400',
                    log.level === 'info' && 'bg-blue-50 text-blue-700 border-l-2 border-blue-400',
                    log.level === 'debug' && 'bg-gray-50 text-gray-500 border-l-2 border-gray-300',
                    log.level === 'log' && 'bg-white text-gray-700 border-l-2 border-gray-200',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <span className={clsx(
                      'shrink-0 uppercase text-[10px] font-bold mt-0.5',
                      log.level === 'error' && 'text-red-500',
                      log.level === 'warn' && 'text-yellow-500',
                      log.level === 'info' && 'text-blue-500',
                      log.level === 'debug' && 'text-gray-400',
                      log.level === 'log' && 'text-gray-400',
                    )}>
                      {log.level === 'error' ? 'ERR' : log.level === 'warn' ? 'WRN' : log.level === 'info' ? 'INF' : log.level === 'debug' ? 'DBG' : 'LOG'}
                    </span>
                    <span className="flex-1 whitespace-pre-wrap">{log.message}</span>
                    <span className="shrink-0 text-[10px] text-gray-400">
                      {log.timestamp ? new Date(Number(log.timestamp)).toLocaleTimeString() : ''}
                    </span>
                  </div>
                  {log.stack && (
                    <pre className="mt-1 text-[10px] text-gray-400 whitespace-pre-wrap overflow-x-auto">{log.stack}</pre>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="p-4 space-y-3 text-xs">
              <div>
                <span className="text-gray-400">Session ID</span>
                <p className="font-mono text-gray-700 break-all">{sessionId}</p>
              </div>
              <div>
                <span className="text-gray-400">Total Events</span>
                <p className="text-gray-700">{(rawEvents || []).length}</p>
              </div>
              <div>
                <span className="text-gray-400">Recording Events</span>
                <p className="text-gray-700">{rrwebEvents.length}</p>
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="p-3 space-y-3 text-xs">
              {!aiAnalysis && !analyzeMutation.isPending && (
                <div className="text-center py-8">
                  <Sparkles className="mx-auto h-8 w-8 text-brand-300" />
                  <p className="mt-2 text-sm font-medium text-gray-700">AI Session Analysis</p>
                  <p className="mt-1 text-gray-400">
                    Analyze this visitor's behavior, intent, friction points, and get recommendations
                  </p>
                  <button
                    onClick={() => analyzeMutation.mutate()}
                    className="mt-4 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
                  >
                    Analyze Session
                  </button>
                  {analyzeMutation.isError && (
                    <p className="mt-2 text-red-500 text-xs">Analysis failed. Try again.</p>
                  )}
                </div>
              )}

              {analyzeMutation.isPending && (
                <div className="text-center py-12">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-500" />
                  <p className="mt-3 text-sm text-gray-500">Analyzing session...</p>
                  <p className="mt-1 text-xs text-gray-400">Extracting journey data and running AI analysis</p>
                </div>
              )}

              {aiAnalysis?.analysis && (
                <div className="space-y-3">
                  {/* Engagement Score */}
                  {aiAnalysis.analysis.engagement_score !== undefined && (
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 p-2.5">
                      <span className="text-gray-500 font-medium">Engagement</span>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className={clsx('h-full rounded-full',
                              (aiAnalysis.analysis.engagement_score || 0) >= 60 ? 'bg-green-500' :
                              (aiAnalysis.analysis.engagement_score || 0) >= 30 ? 'bg-yellow-500' : 'bg-red-500'
                            )}
                            style={{ width: `${aiAnalysis.analysis.engagement_score}%` }}
                          />
                        </div>
                        <span className="font-bold text-gray-900">{aiAnalysis.analysis.engagement_score}/100</span>
                      </div>
                    </div>
                  )}

                  {/* Segment & Conversion */}
                  <div className="grid grid-cols-2 gap-2">
                    {aiAnalysis.analysis.segment && (
                      <div className="rounded-lg bg-brand-50 p-2">
                        <div className="flex items-center gap-1 text-brand-600 mb-0.5">
                          <User className="h-3 w-3" />
                          <span className="font-medium">Segment</span>
                        </div>
                        <p className="text-brand-700">{aiAnalysis.analysis.segment}</p>
                      </div>
                    )}
                    {aiAnalysis.analysis.conversion_likelihood && (
                      <div className={clsx('rounded-lg p-2',
                        aiAnalysis.analysis.conversion_likelihood === 'high' ? 'bg-green-50' :
                        aiAnalysis.analysis.conversion_likelihood === 'medium' ? 'bg-yellow-50' : 'bg-red-50'
                      )}>
                        <div className={clsx('flex items-center gap-1 mb-0.5',
                          aiAnalysis.analysis.conversion_likelihood === 'high' ? 'text-green-600' :
                          aiAnalysis.analysis.conversion_likelihood === 'medium' ? 'text-yellow-600' : 'text-red-600'
                        )}>
                          <Target className="h-3 w-3" />
                          <span className="font-medium">Conversion</span>
                        </div>
                        <p className={clsx(
                          aiAnalysis.analysis.conversion_likelihood === 'high' ? 'text-green-700' :
                          aiAnalysis.analysis.conversion_likelihood === 'medium' ? 'text-yellow-700' : 'text-red-700'
                        )}>
                          {aiAnalysis.analysis.conversion_likelihood}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Intent */}
                  {aiAnalysis.analysis.intent && (
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 font-medium mb-1">
                        <TrendingUp className="h-3 w-3" />
                        User Intent
                      </div>
                      <p className="text-gray-700">{aiAnalysis.analysis.intent}</p>
                    </div>
                  )}

                  {/* Summary */}
                  {aiAnalysis.analysis.summary && (
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 font-medium mb-1">
                        <Info className="h-3 w-3" />
                        Summary
                      </div>
                      <p className="text-gray-700 leading-relaxed">{aiAnalysis.analysis.summary}</p>
                    </div>
                  )}

                  {/* Journey Analysis */}
                  {aiAnalysis.analysis.journey_analysis && (
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 font-medium mb-1">
                        <MousePointerClick className="h-3 w-3" />
                        Journey Analysis
                      </div>
                      <p className="text-gray-700 leading-relaxed">{aiAnalysis.analysis.journey_analysis}</p>
                    </div>
                  )}

                  {/* Friction Points */}
                  {(aiAnalysis.analysis.friction_points || []).length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 text-red-500 font-medium mb-1">
                        <AlertTriangle className="h-3 w-3" />
                        Friction Points
                      </div>
                      <ul className="space-y-1">
                        {(aiAnalysis.analysis.friction_points || []).map((f, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-gray-700">
                            <XCircle className="h-3 w-3 mt-0.5 shrink-0 text-red-400" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Positive Signals */}
                  {(aiAnalysis.analysis.positive_signals || []).length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 text-green-500 font-medium mb-1">
                        <CheckCircle className="h-3 w-3" />
                        Positive Signals
                      </div>
                      <ul className="space-y-1">
                        {(aiAnalysis.analysis.positive_signals || []).map((s, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-gray-700">
                            <CheckCircle className="h-3 w-3 mt-0.5 shrink-0 text-green-400" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Exit Reason */}
                  {aiAnalysis.analysis.exit_reason && (
                    <div>
                      <div className="flex items-center gap-1 text-orange-500 font-medium mb-1">
                        <XCircle className="h-3 w-3" />
                        Why They Left
                      </div>
                      <p className="text-gray-700">{aiAnalysis.analysis.exit_reason}</p>
                    </div>
                  )}

                  {/* Device Impact */}
                  {aiAnalysis.analysis.device_impact && (
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 font-medium mb-1">
                        <Smartphone className="h-3 w-3" />
                        Device Impact
                      </div>
                      <p className="text-gray-700">{aiAnalysis.analysis.device_impact}</p>
                    </div>
                  )}

                  {/* Recommendations */}
                  {(aiAnalysis.analysis.recommendations || []).length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 text-brand-500 font-medium mb-1">
                        <Lightbulb className="h-3 w-3" />
                        Recommendations
                      </div>
                      <ul className="space-y-1.5">
                        {(aiAnalysis.analysis.recommendations || []).map((r, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-gray-700">
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-600">
                              {i + 1}
                            </span>
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Behavioral Signals from Journey */}
                  <div className="border-t border-gray-100 pt-2 mt-2">
                    <p className="text-[10px] font-medium text-gray-400 uppercase mb-1.5">Session Signals</p>
                    <div className="flex flex-wrap gap-1.5">
                      {aiAnalysis.journey.signals.hasRageClicks && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700">
                          {aiAnalysis.journey.signals.rageClickCount} rage clicks
                        </span>
                      )}
                      {aiAnalysis.journey.signals.hasErrors && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700">
                          {aiAnalysis.journey.signals.errorCount} errors
                        </span>
                      )}
                      {aiAnalysis.journey.signals.bounced && (
                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-700">
                          bounced
                        </span>
                      )}
                      {aiAnalysis.journey.signals.reachedCheckout && (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                          reached checkout
                        </span>
                      )}
                      {aiAnalysis.journey.signals.backtrackCount > 0 && (
                        <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-medium text-yellow-700">
                          {aiAnalysis.journey.signals.backtrackCount} backtracks
                        </span>
                      )}
                      {aiAnalysis.journey.signals.deadClickCount > 0 && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700">
                          {aiAnalysis.journey.signals.deadClickCount} dead clicks
                        </span>
                      )}
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                        {aiAnalysis.journey.clickCount} clicks
                      </span>
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                        {aiAnalysis.journey.pageCount} pages
                      </span>
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                        {Math.round(aiAnalysis.journey.signals.averageScrollDepth)}% avg scroll
                      </span>
                    </div>
                  </div>

                  {/* Re-analyze button */}
                  <div className="border-t border-gray-100 pt-2">
                    <button
                      onClick={() => analyzeMutation.mutate()}
                      disabled={analyzeMutation.isPending}
                      className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Re-analyze Session
                    </button>
                  </div>

                  {/* Follow-up queries */}
                  <div className="border-t border-gray-100 pt-2 space-y-2">
                    <div className="flex items-center gap-1 text-gray-500 font-medium">
                      <MessageSquare className="h-3 w-3" />
                      <span>Ask about this session</span>
                    </div>

                    {queryHistory.length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {queryHistory.map((item, i) => (
                          <div key={i} className="space-y-1">
                            <div className="rounded-lg bg-brand-50 px-2.5 py-1.5">
                              <p className="text-[10px] font-medium text-brand-600 mb-0.5">You</p>
                              <p className="text-xs text-brand-800">{item.question}</p>
                            </div>
                            <div className="rounded-lg bg-gray-50 px-2.5 py-1.5">
                              <p className="text-[10px] font-medium text-gray-400 mb-0.5">AI</p>
                              <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{item.answer}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {queryMutation.isPending && (
                      <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-2.5 py-2">
                        <Loader2 className="h-3 w-3 animate-spin text-brand-500 shrink-0" />
                        <p className="text-xs text-gray-400">Analyzing...</p>
                      </div>
                    )}

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const q = queryInput.trim();
                        if (q) queryMutation.mutate(q);
                      }}
                      className="flex gap-1.5"
                    >
                      <input
                        type="text"
                        value={queryInput}
                        onChange={(e) => setQueryInput(e.target.value)}
                        placeholder="e.g. Why did they leave the checkout page?"
                        className="flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs placeholder-gray-400 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        maxLength={1000}
                        disabled={queryMutation.isPending}
                      />
                      <button
                        type="submit"
                        disabled={!queryInput.trim() || queryMutation.isPending}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 transition-colors"
                      >
                        <Send className="h-3 w-3" />
                      </button>
                    </form>
                    {queryMutation.isError && (
                      <p className="text-[10px] text-red-500">Query failed. Try again.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
